import { useEffect, useMemo, useState } from "react";
import { formatCurrency, cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Download,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Package,
  DollarSign,
  Scale,
  Loader2,
  ChevronDown,
  X,
} from "lucide-react";
import { toast } from "sonner";

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState("sales");
  const [period, setPeriod] = useState("7");
  const [selectedChannels, setSelectedChannels] = useState([]); // [] = tüm kanallar
  const [channelPopoverOpen, setChannelPopoverOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [financeLoading, setFinanceLoading] = useState(true);
  const [report, setReport] = useState({
    summary: { total_revenue: 0, total_orders: 0, avg_order_value: 0, total_items: 0 },
    timeline: [],
    channels: [],
    top_products: [],
  });
  const [finance, setFinance] = useState({
    total_income: 0,
    total_expense: 0,
    net: 0,
    income_count: 0,
    expense_count: 0,
    monthly: [],
  });

  useEffect(() => {
    let mounted = true;
    const fetchReports = async () => {
      setLoading(true);
      try {
        const body = {};
        if (period === "this_month") {
          const now = new Date();
          const y = now.getFullYear();
          const m = String(now.getMonth() + 1).padStart(2, "0");
          body.since_date = `${y}-${m}-01`;
        } else {
          body.period_days = parseInt(period, 10);
        }
        if (selectedChannels.length > 0) {
          body.integration_id = selectedChannels.join(",");
        }
        const res = await api.post("/reports/overview", body);
        if (!mounted) return;
        setReport({
          summary: res?.summary || { total_revenue: 0, total_orders: 0, avg_order_value: 0, total_items: 0 },
          timeline: Array.isArray(res?.timeline) ? res.timeline : [],
          channels: Array.isArray(res?.channels) ? res.channels : [],
          top_products: Array.isArray(res?.top_products) ? res.top_products : [],
        });
      } catch (error) {
        if (!mounted) return;
        setReport({
          summary: { total_revenue: 0, total_orders: 0, avg_order_value: 0, total_items: 0 },
          timeline: [],
          channels: [],
          top_products: [],
        });
        toast.error(error?.message || "Rapor verileri alınamadı");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchReports();
    return () => {
      mounted = false;
    };
  }, [period, selectedChannels]);

  useEffect(() => {
    let mounted = true;
    const getAllPages = async (endpoint, perPage = 25) => {
      const first = await api.get(`${endpoint}?page=1&per_page=${perPage}`);
      const rows = first?.invoices || [];
      const total = Number(first?.total || rows.length || 0);
      const totalPages = Math.max(1, Math.ceil(total / perPage));
      const all = [...rows];
      for (let p = 2; p <= totalPages; p += 1) {
        const next = await api.get(`${endpoint}?page=${p}&per_page=${perPage}`);
        all.push(...(next?.invoices || []));
      }
      return all;
    };
    const parseAmount = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const inPeriod = (dateValue) => {
      if (!dateValue) return false;
      const d = new Date(dateValue);
      if (Number.isNaN(d.getTime())) return false;
      const from = new Date();
      if (period === "this_month") {
        from.setDate(1);
        from.setHours(0, 0, 0, 0);
      } else {
        const days = Number(period || 7);
        from.setHours(0, 0, 0, 0);
        from.setDate(from.getDate() - days + 1);
      }
      return d >= from;
    };
    const monthKey = (dateValue) => {
      const d = new Date(dateValue);
      if (Number.isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("tr-TR", { month: "short", year: "2-digit" });
    };

    const fetchFinance = async () => {
      setFinanceLoading(true);
      try {
        let salesRows = [];
        let expenseRows = [];
        try {
          const salesCache = sessionStorage.getItem("sales_invoices_cache_v1");
          const parsed = salesCache ? JSON.parse(salesCache) : null;
          if (parsed?.rows?.length) salesRows = parsed.rows;
        } catch {}
        try {
          const incomingCache = sessionStorage.getItem("incoming_e_invoices_cache_v1");
          const parsed = incomingCache ? JSON.parse(incomingCache) : null;
          if (parsed?.rows?.length) expenseRows = parsed.rows;
        } catch {}

        if (!salesRows.length) salesRows = await getAllPages("/parasut/invoices");
        if (!expenseRows.length) expenseRows = await getAllPages("/parasut/incoming-e-invoices");

        const filteredSales = salesRows.filter((x) => inPeriod(x.issue_date));
        const filteredExpenses = expenseRows.filter((x) => inPeriod(x.issue_date));

        const totalIncome = filteredSales.reduce((acc, x) => acc + parseAmount(x.display_gross_total ?? x.gross_total), 0);
        const totalExpense = filteredExpenses.reduce((acc, x) => acc + parseAmount(x.gross_total), 0);

        const monthlyMap = new Map();
        filteredSales.forEach((x) => {
          const k = monthKey(x.issue_date);
          const prev = monthlyMap.get(k) || { month: k, income: 0, expense: 0 };
          prev.income += parseAmount(x.display_gross_total ?? x.gross_total);
          monthlyMap.set(k, prev);
        });
        filteredExpenses.forEach((x) => {
          const k = monthKey(x.issue_date);
          const prev = monthlyMap.get(k) || { month: k, income: 0, expense: 0 };
          prev.expense += parseAmount(x.gross_total);
          monthlyMap.set(k, prev);
        });

        if (!mounted) return;
        setFinance({
          total_income: totalIncome,
          total_expense: totalExpense,
          net: totalIncome - totalExpense,
          income_count: filteredSales.length,
          expense_count: filteredExpenses.length,
          monthly: Array.from(monthlyMap.values()),
        });
      } catch (error) {
        if (!mounted) return;
        setFinance({
          total_income: 0,
          total_expense: 0,
          net: 0,
          income_count: 0,
          expense_count: 0,
          monthly: [],
        });
        toast.error(error?.message || "Gelir/gider verileri alınamadı");
      } finally {
        if (mounted) setFinanceLoading(false);
      }
    };

    fetchFinance();
    return () => {
      mounted = false;
    };
  }, [period]);

  const salesData = useMemo(
    () =>
      (report.timeline || []).map((row) => ({
        month: row.label,
        revenue: Number(row.revenue || 0),
        orders: Number(row.orders || 0),
      })),
    [report.timeline]
  );
  const channelData = useMemo(
    () =>
      (report.channels || []).map((row) => ({
        provider: row.provider,
        name: row.name,
        value: Number(row.value || 0),
        color: row.color || "#94a3b8",
        revenue: Number(row.revenue || 0),
        orders: Number(row.orders || 0),
        isDeleted: Boolean(row.is_deleted),
      })),
    [report.channels]
  );
  const topProducts = useMemo(
    () =>
      (report.top_products || []).map((row) => ({
        name: row.name,
        sales: Number(row.sales || 0),
        revenue: Number(row.revenue || 0),
      })),
    [report.top_products]
  );

  const totalRevenue = Number(report.summary?.total_revenue || 0);
  const totalOrders = Number(report.summary?.total_orders || 0);
  const avgOrderValue = Number(report.summary?.avg_order_value || 0);
  const totalItems = Number(report.summary?.total_items || 0);

  const exportCsv = () => {
    const rows = [
      ["Rapor Periyodu (Gün)", period],
      ["Toplam Ciro", totalRevenue.toFixed(2)],
      ["Toplam Sipariş", String(totalOrders)],
      ["Ortalama Sipariş", avgOrderValue.toFixed(2)],
      ["Satılan Ürün", String(totalItems)],
      [],
      ["Kanal", "Silinmiş", "Ciro", "Sipariş", "Pay %"],
      ...channelData.map((c) => [c.name, c.isDeleted ? "Evet" : "Hayır", c.revenue.toFixed(2), String(c.orders), String(c.value)]),
      [],
      ["Ürün", "Satış Adedi", "Ciro"],
      ...topProducts.map((p) => [p.name, String(p.sales), p.revenue.toFixed(2)]),
    ];
    const csv = rows.map((r) => r.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `raporlar-${period}gun.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-shell space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="page-header">
        <div className="app-page-header">
          <h1 className="app-page-title">Raporlar</h1>
          <p className="app-page-subtitle">Satış ve performans analizleriniz.</p>
        </div>
        <div className="page-header-actions flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">Bu Ay</SelectItem>
              <SelectItem value="7">Son 7 Gün</SelectItem>
              <SelectItem value="30">Son 30 Gün</SelectItem>
              <SelectItem value="90">Son 3 Ay</SelectItem>
              <SelectItem value="365">Son 1 Yıl</SelectItem>
            </SelectContent>
          </Select>
          {report.channels?.length > 0 && (
            <Popover open={channelPopoverOpen} onOpenChange={setChannelPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="channel-filter-select"
                  className="w-[180px] justify-between font-normal"
                >
                  <span className="truncate">
                    {selectedChannels.length === 0
                      ? "Tüm Kanallar"
                      : selectedChannels.length === 1
                      ? (report.channels.find(
                          (c) => (c.integration_id || c.provider) === selectedChannels[0]
                        )?.name ?? selectedChannels[0])
                      : `${selectedChannels.length} kanal seçili`}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-2" align="start">
                <div className="space-y-1">
                  <button
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors",
                      selectedChannels.length === 0 && "bg-muted font-medium"
                    )}
                    onClick={() => { setSelectedChannels([]); setChannelPopoverOpen(false); }}
                  >
                    Tüm Kanallar
                  </button>
                  <div className="my-1 h-px bg-border" />
                  {report.channels.map((ch) => {
                    const key = ch.integration_id || ch.provider;
                    const checked = selectedChannels.includes(key);
                    return (
                      <label
                        key={key}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            setSelectedChannels((prev) =>
                              v ? [...prev, key] : prev.filter((x) => x !== key)
                            );
                          }}
                        />
                        <span className="truncate">{ch.name}</span>
                      </label>
                    );
                  })}
                  {selectedChannels.length > 0 && (
                    <>
                      <div className="my-1 h-px bg-border" />
                      <button
                        className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted transition-colors"
                        onClick={() => setSelectedChannels([])}
                      >
                        <X className="h-3 w-3" /> Seçimi temizle
                      </button>
                    </>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
          <Button variant="outline" data-testid="export-report-btn" onClick={exportCsv} disabled={loading}>
            <Download className="mr-2 h-4 w-4" />
            Rapor İndir
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2 border-b border-border pb-0">
        {[["sales","Satış Performansı"],["finance","Gelir / Gider"]].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={cn(
              "px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
              activeTab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >{label}</button>
        ))}
      </div>

      {activeTab === "sales" ? (
        <>
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="app-stat-card" data-testid="stat-revenue">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Toplam Ciro</p>
                <p className="app-stat-value">{formatCurrency(totalRevenue)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="app-stat-card" data-testid="stat-orders">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Toplam Sipariş</p>
                <p className="app-stat-value">{totalOrders}</p>
              </div>
              <ShoppingCart className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="app-stat-card" data-testid="stat-avg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ort. Sipariş</p>
                <p className="app-stat-value">{formatCurrency(avgOrderValue)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="app-stat-card" data-testid="stat-products">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Satılan Ürün</p>
                <p className="app-stat-value">{totalItems}</p>
              </div>
              <Package className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Sales Chart */}
        <div className="surface-card overflow-hidden" data-testid="sales-chart">
          <div className="sheet-heading">
            <h2>Aylık Satış Trendi</h2>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="h-[300px] grid place-items-center text-sm text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : salesData.length === 0 ? (
              <div className="h-[300px] grid place-items-center text-sm text-muted-foreground">
                Rapor verisi bulunamadı.
              </div>
            ) : (
              <div className="h-[300px] w-full min-w-0">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="month" className="text-xs" />
                    <YAxis
                      tickFormatter={(value) => `₺${(value / 1000).toFixed(0)}K`}
                      className="text-xs"
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="rounded-lg bg-background border border-border p-3 shadow-sm">
                              <p className="font-medium">{payload[0].payload.month}</p>
                              <p className="text-sm text-primary">
                                Ciro: {formatCurrency(payload[0].value)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {payload[0].payload.orders} sipariş
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Channel Distribution */}
        <div className="surface-card overflow-hidden" data-testid="channel-chart">
          <div className="sheet-heading">
            <h2>Kanal Dağılımı</h2>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="h-[300px] grid place-items-center text-sm text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : channelData.length === 0 ? (
              <div className="h-[300px] grid place-items-center text-sm text-muted-foreground">
                Kanal dağılımı verisi yok.
              </div>
            ) : (
              <div className="h-[300px] w-full min-w-0 flex items-center">
                <ResponsiveContainer width="60%" height={300}>
                  <PieChart>
                    <Pie
                      data={channelData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {channelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="rounded-lg bg-background border border-border p-3 shadow-sm">
                              <p className="font-medium">
                                {payload[0].payload.name}
                                {payload[0].payload.isDeleted ? " (Silinmiş)" : ""}
                              </p>
                              <p className="text-sm">%{payload[0].value}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {channelData.map((channel, index) => (
                    <div key={`${channel.provider}-${channel.name}-${index}`} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: channel.color }}
                      />
                      <span className={`text-sm ${channel.isDeleted ? "line-through text-muted-foreground" : ""}`}>
                        {channel.name}
                      </span>
                      {channel.isDeleted ? <span className="text-[11px] text-muted-foreground">(Silinmiş)</span> : null}
                      <span className="text-sm text-muted-foreground">%{channel.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Products */}
      <div className="table-shell overflow-hidden" data-testid="top-products">
        <div className="sheet-heading">
          <h2>En Çok Satan Ürünler</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-5 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">#</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Ürün</th>
              <th className="px-4 py-3 text-right text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Adet</th>
              <th className="px-5 py-3 text-right text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Ciro</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-sm text-muted-foreground">
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              </td></tr>
            ) : topProducts.length === 0 ? (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-sm text-muted-foreground">
                En çok satan ürün verisi yok.
              </td></tr>
            ) : (
              topProducts.map((product, index) => (
                <tr key={index} className="hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-3.5">
                    <span className="w-7 h-7 rounded-full bg-primary/10 inline-flex items-center justify-center text-xs font-semibold text-primary">
                      {index + 1}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 font-medium">{product.name}</td>
                  <td className="px-4 py-3.5 text-right text-muted-foreground">{product.sales}</td>
                  <td className="px-5 py-3.5 text-right font-semibold tabular-nums">{formatCurrency(product.revenue)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
        </>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="app-stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Gelir</p>
                    <p className="app-stat-value">{formatCurrency(finance.total_income)}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="app-stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Gider</p>
                    <p className="app-stat-value">{formatCurrency(finance.total_expense)}</p>
                  </div>
                  <TrendingDown className="h-8 w-8 text-rose-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="app-stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Net</p>
                    <p className="app-stat-value">{formatCurrency(finance.net)}</p>
                  </div>
                  <Scale className="h-8 w-8 text-sky-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="app-stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Kayıt (Gelir/Gider)</p>
                    <p className="app-stat-value">{finance.income_count} / {finance.expense_count}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="surface-card overflow-hidden">
            <div className="sheet-heading">
              <h2>Dönemsel Gelir / Gider Dağılımı</h2>
            </div>
            <div className="p-6">
              {financeLoading ? (
                <div className="h-[320px] grid place-items-center text-sm text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : finance.monthly.length === 0 ? (
                <div className="h-[320px] grid place-items-center text-sm text-muted-foreground">
                  Gelir/gider verisi bulunamadı.
                </div>
              ) : (
                <div className="h-[320px] w-full">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={finance.monthly}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="month" className="text-xs" />
                      <YAxis className="text-xs" />
                      <Tooltip />
                      <Bar dataKey="income" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="expense" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
